import React, { useState, useEffect } from 'react';
import { InputMask } from '@react-input/mask';
import CurrencyInput from "react-currency-input-field";
import Select from "react-select"
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import api from '../../../config/axios';
import Swal from 'sweetalert2';
import DatePicker, { dateHelpers } from '../../../components/utils/datepicker';

import { autoCompleteClientes, autoCompleteServicos } from "../../../components/utils/autocompletes";
import { FaRegSave, FaTimes, FaArrowRight, FaEdit, FaTrashAlt, FaPlus, FaUserPlus, FaUserMinus } from 'react-icons/fa';

const AgendamentoModal = ({ onClose, agendamento, onAgendamentoAtualizado }) => {
    const [tituloModal, setTituloModal] = useState("");
    const [servicosExcluidos, setServicosExcluidos] = useState([]);
    const [assistentesExcluidos, setAssistentesExcluidos] = useState([]);
    const { inputPesquisaCliente, clientesOptions, handleAutoCompleteClientes, handleFocusClientes } = autoCompleteClientes();
    const { inputPesquisaServico, servicosOptions, handleAutoCompleteServicos, handleFocusServicos } = autoCompleteServicos();
    const navigate = useNavigate();

    const statusOptions = [
        { label: "Agendado", value: 'agendado', className: "bg-agendado" },
        { label: "Confirmado", value: 'confirmado', className: "bg-confirmado" },
        { label: "Aguardando", value: 'aguardando', className: "bg-aguardando" },
        { label: "Em Atendimento", value: 'em atendimento', className: "bg-em-atendimento" },
        { label: "Pago", value: 'pago', className: "bg-pago" },
        { label: "Finalizado", value: 'finalizado', className: "bg-finalizado" },
        { label: "Cancelado", value: 'cancelado', className: "bg-cancelado" },
        { label: "Faltou", value: 'faltou', className: "bg-faltou" },
    ];

    const getPermittedStatus = (currentStatus) => {
        if (!currentStatus || currentStatus === '') {
            return ['agendado'];
        }

        const statusFlow = {
            'agendado': ['agendado', 'confirmado', 'cancelado', 'faltou'],
            'confirmado': ['confirmado', 'aguardando', 'em atendimento', 'cancelado', 'faltou'],
            'aguardando': ['aguardando', 'em atendimento', 'cancelado', 'faltou'],
            'em atendimento': ['em atendimento', 'pago', 'finalizado', 'cancelado'],
            'pago': ['pago', 'finalizado'],
            'finalizado': ['finalizado'],
            'cancelado': ['cancelado'],
            'faltou': ['faltou']
        };

        return statusFlow[currentStatus] || [currentStatus];
    };

    const [formData, setFormData] = useState({
        id: '',
        cliente: '',
        data: new Date(),
        servicos: [],
        status: 'agendado',
        observacao: ''
    });

    const customStyles = {
        menu: (provided) => ({
            ...provided,
            whiteSpace: "pre-line",
        }),
        option: (provided) => ({
            ...provided,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        }),
    };

    useEffect(() => {
        if (agendamento != null) {
            const servicosComColaboradores = (agendamento.servicos || []).map(servico => ({
                ...servico,
                editar: false,
                colaboradoresAssociados: [],
                inputPesquisa: '',
                assistentes: servico.assistentes || []
            }));

            setFormData({
                ...agendamento,
                data: agendamento.data ? dateHelpers.stringToDate(agendamento.data) : new Date(),
                servicos: servicosComColaboradores
            });

            setTituloModal('Editar Agendamento');

            if (agendamento.servicos && agendamento.servicos.length > 0 && agendamento.servicos[0].servico) {
                handleAutoCompleteServicos(agendamento.servicos[0].servico.label);

                servicosComColaboradores.forEach(async (servico, index) => {
                    if (servico.servico && servico.servico.value) {
                        await obterColaboradoresAssociados(servico.servico.value, servico.id);

                        if (servico.id && !String(servico.id).startsWith('temp_')) {
                            await carregarAssistentesExistentes(servico.id, index);
                        }
                    }
                });
            }
        }
        else {
            setTituloModal('Novo Agendamento');
            setFormData(prevState => ({
                ...prevState,
                servicos: [{
                    id: `temp_${new Date().getTime()}`,
                    servico: null,
                    profissional: null,
                    tempo: '',
                    inicio: '00:00',
                    fim: '00:00',
                    valor: '',
                    editar: true,
                    colaboradoresAssociados: [],
                    inputPesquisa: '',
                    assistentes: []
                }]
            }));
        }
    }, [agendamento]);



    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'status') {
            if (agendamento === null) {
                if (value !== 'agendado') {
                    Swal.fire(
                        'Aviso',
                        'Para um novo agendamento, apenas o status "Agendado" é permitido.',
                        'warning'
                    );
                    return;
                }
            } else {
                const permittedStatus = getPermittedStatus(agendamento?.status || '');
                if (!permittedStatus.includes(value)) {
                    Swal.fire(
                        'Aviso',
                        `Não é possível voltar para o status "${value}". Apenas é permitido avançar no fluxo de status.`,
                        'warning'
                    );
                    return;
                }
            }
        }

        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSelectChange = async (name, value) => {
        setFormData((prevState) => ({
            ...prevState,
            [name]: value || "",
        }));
    };

    const handleDateChange = (date) => {
        setFormData({
            ...formData,
            data: date
        });
    };

    const handleChangeServico = (id, field, value) => {
        if (field === 'servico') {
            if (value == null) {
                setFormData((prevState) => ({
                    ...prevState,
                    servicos: prevState.servicos.map((servico) =>
                        servico.id === id ? {
                            ...servico,
                            servico: null,
                            profissional: null,
                            valor: '',
                            tempo: '',
                            inicio: '00:00',
                            fim: '00:00',
                            colaboradoresAssociados: [],
                            inputPesquisa: '',
                            assistentes: []
                        } : servico
                    ),
                }));
                return;
            }

            const servicoSelecionado = servicosOptions.find(x => x.value === value.value);
            setFormData((prevFormData) => ({
                ...prevFormData,
                servicos: prevFormData.servicos.map((servico) =>
                    servico.id === id ? {
                        ...servico,
                        [field]: value,
                        valor: servicoSelecionado.preco,
                        tempo: servicoSelecionado.tempo.substring(0, 5),
                        profissional: null,
                        inputPesquisa: value.label
                    } : servico
                ),
            }));
            obterColaboradoresAssociados(servicoSelecionado.value, id);
            return;
        }

        setFormData((prevFormData) => ({
            ...prevFormData,
            servicos: prevFormData.servicos.map((servico) =>
                servico.id === id ? { ...servico, [field]: value } : servico
            ),
        }));



        if (field === 'inicio' && value !== '00:00') {
            const servicoAtual = formData.servicos.find(s => s.id === id);
            if (servicoAtual && servicoAtual.tempo && servicoAtual.tempo !== '00:00') {
                const [inicioHoras, inicioMinutos] = value.split(':').map(Number);
                const [tempoHoras, tempoMinutos] = servicoAtual.tempo.split(':').map(Number);

                let finalMinutos = inicioMinutos + tempoMinutos;
                let finalHoras = inicioHoras + tempoHoras + Math.floor(finalMinutos / 60);
                finalMinutos = finalMinutos % 60;
                finalHoras = finalHoras % 24;

                const fim = `${String(finalHoras).padStart(2, '0')}:${String(finalMinutos).padStart(2, '0')}`;



                setFormData((prevFormData) => ({
                    ...prevFormData,
                    servicos: prevFormData.servicos.map((servico) =>
                        servico.id === id ? { ...servico, fim } : servico
                    ),
                }));
            }
        }




    };

    const handleAdicionarServico = () => {
        handleAutoCompleteServicos('');
        const novoServico = {
            id: `temp_${new Date().getTime()}`,
            servico: null,
            profissional: null,
            tempo: '',
            inicio: '00:00',
            fim: '00:00',
            valor: '',
            editar: true,
            colaboradoresAssociados: [],
            inputPesquisa: '',
            assistentes: []
        };

        setFormData((prevState) => ({
            ...prevState,
            servicos: [...prevState.servicos, novoServico],
        }));
    };

    const handleEditarServico = (servico) => {
        setFormData((prevState) => ({
            ...prevState,
            servicos: prevState.servicos.map((s) =>
                s.id === servico.id ? { ...s, editar: !s.editar } : s
            ),
        }));
    };

    const handleRemoverServico = (servico) => {
        Swal.fire({
            title: 'Tem certeza?',
            text: 'Deseja remover este serviço do agendamento?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, remover',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                if (servico.id && !String(servico.id).startsWith('temp_') && agendamento != null) {
                    setServicosExcluidos(prev => [...prev, servico.id]);
                }

                setFormData((prevState) => ({
                    ...prevState,
                    servicos: prevState.servicos.filter((s) => s.id !== servico.id),
                }));
            }
        });
    };



    const obterColaboradoresAssociados = async (value, servicoInternalId) => {
        const response = await api.get(`/servicos/details/${value}`);

        const colaboradoresMapeados = response.data.colaboradoresAssociados.records.map(colaborador => ({
            id: colaborador.id_pessoa,
            nome: colaborador.pessoa.nome,
            comissao: colaborador.comissao,
            tempo: colaborador.tempo
        }));

        setFormData((prevFormData) => ({
            ...prevFormData,
            servicos: prevFormData.servicos.map((servico) =>
                servico.id === servicoInternalId ? {
                    ...servico,
                    colaboradoresAssociados: colaboradoresMapeados
                } : servico
            ),
        }));
    };

    const handleAdicionarAssistente = (servicoId) => {
        setFormData((prevState) => ({
            ...prevState,
            servicos: prevState.servicos.map((servico) =>
                servico.id === servicoId
                    ? {
                        ...servico,
                        assistentes: [...(servico.assistentes || []), { id: `temp_${new Date().getTime()}`, profissional: null }]
                    }
                    : servico
            ),
        }));
    };

    const handleRemoverAssistente = (servicoId, assistenteId) => {
        const servicoAtual = formData.servicos.find(s => s.id === servicoId);
        const assistenteRemovido = servicoAtual?.assistentes.find(a => a.id === assistenteId);

        if (assistenteRemovido && !String(assistenteRemovido.id).startsWith('temp_') && agendamento != null) {
            setAssistentesExcluidos(prev => [...prev, assistenteRemovido.id]);
        }

        setFormData((prevState) => ({
            ...prevState,
            servicos: prevState.servicos.map((servico) =>
                servico.id === servicoId
                    ? {
                        ...servico,
                        assistentes: servico.assistentes.filter(a => a.id !== assistenteId)
                    }
                    : servico
            ),
        }));
    };

    const handleChangeAssistente = (servicoId, assistenteId, value) => {
        setFormData((prevState) => ({
            ...prevState,
            servicos: prevState.servicos.map((servico) =>
                servico.id === servicoId
                    ? {
                        ...servico,
                        assistentes: servico.assistentes.map(a =>
                            a.id === assistenteId
                                ? { ...a, profissional: value }
                                : a
                        )
                    }
                    : servico
            ),
        }));
    };

    const getColaboradoresDisponiveis = (servico) => {
        const profissionalPrincipalId = servico.profissional?.value;
        const assistentesIds = (servico.assistentes || []).map(a => a.profissional?.value).filter(Boolean);

        return servico.colaboradoresAssociados.filter(colab =>
            colab.id !== profissionalPrincipalId && !assistentesIds.includes(colab.id)
        );
    };

    const carregarAssistentesExistentes = async (servicoId, servicoIndex) => {
        const response = await api.get(`/agendamento-servico-colaborador/servico/${servicoId}`);

        if (response.data && response.data.length > 0) {
            const assistentesMapeados = response.data
                .filter(colab => colab.tipo_colaborador === 'assistente')
                .map(colab => ({
                    id: colab.id,
                    profissional: {
                        label: colab.colaborador?.nome || 'Colaborador',
                        value: colab.id_pessoa_colaborador
                    }
                }));

            setFormData((prevFormData) => ({
                ...prevFormData,
                servicos: prevFormData.servicos.map((servico, index) =>
                    index === servicoIndex
                        ? { ...servico, assistentes: assistentesMapeados }
                        : servico
                ),
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const camposObrigatorios = [
            { campo: 'cliente', label: 'Cliente' },
            { campo: 'data', label: 'Data' },
            { campo: 'status', label: 'Status' }
        ];

        const camposInvalidos = camposObrigatorios.filter(({ campo }) => {
            const valor = formData[campo];

            if (campo === 'data') {
                return !valor || !(valor instanceof Date) || isNaN(valor.getTime());
            }

            if (typeof valor === 'string') return !valor.trim();

            if (typeof valor === 'object' && valor !== null) return !valor.value;

            return !valor;
        });

        if (camposInvalidos.length > 0) {
            Swal.fire(
                'Erro',
                `Os seguintes campos são obrigatórios: ${camposInvalidos.map(c => c.label).join(', ')}`,
                'error'
            );
            return;
        }

        if (formData.servicos.length === 0) {
            Swal.fire('Erro', 'Adicione pelo menos um serviço ao agendamento', 'error');
            return;
        }

        for (let i = 0; i < formData.servicos.length; i++) {
            const servico = formData.servicos[i];

            if (!servico.servico || !servico.servico.value) {
                Swal.fire('Erro', `O serviço ${i + 1} não foi selecionado`, 'error');
                return;
            }

            if (!servico.profissional || !servico.profissional.value) {
                Swal.fire('Erro', `O serviço ${i + 1} não possui profissional selecionado`, 'error');
                return;
            }

            if (!servico.tempo || servico.tempo === '00:00') {
                Swal.fire('Erro', `O serviço ${i + 1} não possui tempo definido`, 'error');
                return;
            }

            if (!servico.inicio || servico.inicio === '00:00') {
                Swal.fire('Erro', `O serviço ${i + 1} não possui horário de início definido`, 'error');
                return;
            }

            if (!servico.fim || servico.fim === '00:00') {
                Swal.fire('Erro', `O serviço ${i + 1} não possui horário de fim definido`, 'error');
                return;
            }

            if (!servico.valor || servico.valor === '' || servico.valor === '0' || servico.valor === '0,00') {
                Swal.fire('Erro', `O serviço ${i + 1} não possui valor definido`, 'error');
                return;
            }
        }

        for (let i = 0; i < formData.servicos.length; i++) {
            const servico = formData.servicos[i];

            if (servico.assistentes && servico.assistentes.length > 0) {
                const assistentesIds = servico.assistentes
                    .filter(a => a.profissional && a.profissional.value)
                    .map(a => a.profissional.value);

                const assistentesDuplicados = assistentesIds.filter((id, index) => assistentesIds.indexOf(id) !== index);

                if (assistentesDuplicados.length > 0) {
                    Swal.fire('Erro', `O serviço ${i + 1} possui assistentes duplicados`, 'error');
                    return;
                }

                if (servico.profissional && assistentesIds.includes(servico.profissional.value)) {
                    Swal.fire('Erro', `No serviço ${i + 1}, o profissional principal não pode ser também assistente`, 'error');
                    return;
                }
            }
        }

        for (let i = 0; i < formData.servicos.length; i++) {
            const servicoAtual = formData.servicos[i];

            for (let j = i + 1; j < formData.servicos.length; j++) {
                const outroServico = formData.servicos[j];

                const [inicioAtualH, inicioAtualM] = servicoAtual.inicio.split(':').map(Number);
                const [fimAtualH, fimAtualM] = servicoAtual.fim.split(':').map(Number);
                const [inicioOutroH, inicioOutroM] = outroServico.inicio.split(':').map(Number);
                const [fimOutroH, fimOutroM] = outroServico.fim.split(':').map(Number);

                const inicioAtualMin = inicioAtualH * 60 + inicioAtualM;
                const fimAtualMin = fimAtualH * 60 + fimAtualM;
                const inicioOutroMin = inicioOutroH * 60 + inicioOutroM;
                const fimOutroMin = fimOutroH * 60 + fimOutroM;

                const temConflito = (
                    (inicioAtualMin >= inicioOutroMin && inicioAtualMin < fimOutroMin) ||
                    (fimAtualMin > inicioOutroMin && fimAtualMin <= fimOutroMin) ||
                    (inicioAtualMin <= inicioOutroMin && fimAtualMin >= fimOutroMin) ||
                    (inicioOutroMin <= inicioAtualMin && fimOutroMin >= fimAtualMin)
                );

                if (temConflito) {
                    const nomeServicoAtual = servicoAtual.servico?.label || `Serviço ${i + 1}`;
                    const nomeOutroServico = outroServico.servico?.label || `Serviço ${j + 1}`;

                    Swal.fire(
                        'Conflito de Horário',
                        `Existe conflito de horário entre os serviços:\n\n` +
                        `• ${nomeServicoAtual} (${servicoAtual.inicio} - ${servicoAtual.fim})\n` +
                        `• ${nomeOutroServico} (${outroServico.inicio} - ${outroServico.fim})\n\n` +
                        `Por favor, ajuste os horários para evitar sobreposição.`,
                        'error'
                    );
                    return;
                }
            }
        }

        if (!formData.data || !formData.data instanceof Date || isNaN(formData.data.getTime())) {
            Swal.fire(
                'Erro',
                'É obrigatório informar uma data válida',
                'error'
            );
            return;
        }

        const dia = String(formData.data.getDate()).padStart(2, '0');
        const mes = String(formData.data.getMonth() + 1).padStart(2, '0');
        const ano = formData.data.getFullYear();

        const data = {
            id_pessoa_cliente: formData.cliente.value,
            data: `${ano}-${mes}-${dia}`,
            status: formData.status,
            observacao: formData.observacao,
            servicos: []
        };

        for (let i = 0; i < formData.servicos.length; i++) {
            const servico = formData.servicos[i];
            const valor = parseFloat(servico.valor.toString().replace('R$', '').replace(',', '.').trim());

            const servicoData = {
                id_servico: servico.servico.value,
                id_pessoa_colaborador: servico.profissional.value,
                tempo: servico.tempo,
                hora_inicio: servico.inicio,
                hora_termino: servico.fim,
                valor: valor
            };

            if (servico.assistentes && servico.assistentes.length > 0) {
                servicoData.assistentes = servico.assistentes
                    .filter(a => a.profissional && a.profissional.value)
                    .map(a => {
                        const assistenteData = {
                            id_pessoa_colaborador: a.profissional.value
                        };

                        if (agendamento != null && a.id && !String(a.id).startsWith('temp_')) {
                            assistenteData.id = a.id;
                        }

                        return assistenteData;
                    });
            }

            if (agendamento != null && servico.id && !String(servico.id).startsWith('temp_')) {
                servicoData.id = servico.id;
            }

            data.servicos.push(servicoData);
        }

        Swal.fire({
            title: "Carregando...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const validacaoData = {
                data: `${ano}-${mes}-${dia}`,
                servicos: formData.servicos.map(servico => ({
                    id_pessoa_colaborador: servico.profissional.value,
                    hora_inicio: servico.inicio,
                    hora_termino: servico.fim,
                    ...(agendamento != null && servico.id && !String(servico.id).startsWith('temp_') && { id: servico.id })
                }))
            };

            const validacaoResponse = await api.post('/agendamentos/validar-horarios', validacaoData);

            if (!validacaoResponse.data.valido) {
                Swal.close();
                Swal.fire(
                    'Conflito de Horário',
                    validacaoResponse.data.descricao,
                    'error'
                );
                return;
            }

            if (servicosExcluidos.length > 0 && agendamento != null) {
                await api.delete(`/agendamentos/${agendamento.id_agendamento}/servicos`, {
                    data: { servicosIds: servicosExcluidos }
                });
            }

            if (assistentesExcluidos.length > 0 && agendamento != null) {
                for (const assistenteId of assistentesExcluidos) {
                    await api.delete(`/agendamento-servico-colaborador/${assistenteId}`);
                }
            }

            if (agendamento != null) {
                await api.put(`/agendamentos/${agendamento.id_agendamento}`, data);
            }
            else {
                await api.post(`/agendamentos`, data);
            }
            onAgendamentoAtualizado();
            onClose();
            Swal.fire("Sucesso", "Agendamento salvo com sucesso!", "success");
        }
        catch (error) {
            Swal.close();
            Swal.fire('Erro', `${error.response.data.message}`, 'error');
        }
    };

    const handleAbrirComanda = () => {
        const statusFechados = ['pago', 'finalizado', 'cancelado', 'faltou'];
        const tab = statusFechados.includes(formData.status) ? 'finalizadas' : 'abertas';
        const rotaComanda = `/financeiro/comandas?tab=${tab}&comanda=${agendamento.id_comanda}`;

        navigate(rotaComanda);
        onClose();
    };

    const isFieldsDisabled = () => {
        const statusComEdicaoLimitada = ['em atendimento', 'pago', 'finalizado', 'cancelado', 'faltou'];
        return statusComEdicaoLimitada.includes(formData.status);
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">{tituloModal}</h4>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row mt-3 align-items-end">
                                <div className="col-lg-6 col-12">
                                    <label className="form-label fw-bold">Cliente</label>
                                    <Select
                                        options={clientesOptions}
                                        styles={customStyles}
                                        onInputChange={(value) => {
                                            handleAutoCompleteClientes(value);
                                        }}
                                        onFocus={handleFocusClientes}
                                        onChange={(value) => handleSelectChange('cliente', value)}
                                        placeholder="Digite o nome do cliente"
                                        isClearable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        noOptionsMessage={() =>
                                            inputPesquisaCliente.length < 3 ? null : "Sem opções"
                                        }
                                        value={formData.cliente}
                                        isDisabled={isFieldsDisabled()}
                                    />
                                </div>
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Data</label>
                                    <DatePicker
                                        selected={formData.data}
                                        onChange={handleDateChange}
                                        className="form-control"
                                        disabled={agendamento != null}
                                        placeholderText="DD/MM/AAAA"
                                    />
                                </div>

                                {formData.id_comanda != null ? (
                                    <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={handleAbrirComanda}
                                        >
                                            <FaArrowRight /> Abrir Comanda N° {formData.id_comanda}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                            <div className="row mt-5">
                                <div className="col-12">
                                    <div className="table-responsive" style={{ maxHeight: '45vh' }}>
                                        <table className="table table-striped">
                                            <thead className="table-custom-primary">
                                                <tr>
                                                    <th>Serviço</th>
                                                    <th>Profissional</th>
                                                    <th>Tempo</th>
                                                    <th>Início</th>
                                                    <th>Fim</th>
                                                    <th>Valor (R$)</th>
                                                    <th className="text-center">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.servicos.map((servico) => (
                                                    <tr key={servico.id}>
                                                        <td>
                                                            <div style={{ width: '300px' }}>
                                                                <Select
                                                                    options={servicosOptions}
                                                                    styles={customStyles}
                                                                    onInputChange={(value) => {
                                                                        setFormData((prevFormData) => ({
                                                                            ...prevFormData,
                                                                            servicos: prevFormData.servicos.map((s) =>
                                                                                s.id === servico.id ? { ...s, inputPesquisa: value } : s
                                                                            ),
                                                                        }));
                                                                        handleAutoCompleteServicos(value);
                                                                    }}
                                                                    onFocus={handleFocusServicos}
                                                                    onChange={(value) => handleChangeServico(servico.id, "servico", value)}
                                                                    placeholder="Digite o nome do serviço"
                                                                    isClearable
                                                                    menuPlacement="auto"
                                                                    menuPosition="fixed"
                                                                    isDisabled={!servico.editar || isFieldsDisabled()}
                                                                    noOptionsMessage={() => "Sem opções"}
                                                                    value={servico.servico}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ width: '300px' }}>
                                                                <select
                                                                    name="profissional"
                                                                    className="form-control"
                                                                    value={servico.profissional?.value || ''}
                                                                    disabled={!servico.servico || !servico.colaboradoresAssociados.length || !servico.editar || isFieldsDisabled()}
                                                                    onChange={(e) => {
                                                                        const colaborador = servico.colaboradoresAssociados.find(c => c.id == e.target.value);
                                                                        const profissionalObj = colaborador ? {
                                                                            label: colaborador.nome,
                                                                            value: colaborador.id
                                                                        } : null;

                                                                        setFormData((prevFormData) => ({
                                                                            ...prevFormData,
                                                                            servicos: prevFormData.servicos.map((s) =>
                                                                                s.id === servico.id
                                                                                    ? { ...s, profissional: profissionalObj, assistentes: [] }
                                                                                    : s
                                                                            ),
                                                                        }));
                                                                    }}
                                                                >
                                                                    <option value="" hidden>Selecione...</option>
                                                                    {servico.colaboradoresAssociados.map((option, index) => (
                                                                        <option key={index} value={option.id}>
                                                                            {option.nome}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                                {(servico.assistentes || []).map((assistente) => {
                                                                    const colaboradoresDisponiveis = getColaboradoresDisponiveis(servico);
                                                                    return (
                                                                        <div key={assistente.id} className="mt-3" style={{ width: '300px' }}>
                                                                            <label className="form-label fw-bold">Assistente</label>
                                                                            <div className="d-flex align-items-center">
                                                                                <select
                                                                                    className="form-control flex-grow-1"
                                                                                    value={assistente.profissional?.value || ''}
                                                                                    onChange={(e) => {
                                                                                        const colaborador = servico.colaboradoresAssociados.find(c => c.id == e.target.value);
                                                                                        const profissionalObj = colaborador ? {
                                                                                            label: colaborador.nome,
                                                                                            value: colaborador.id
                                                                                        } : null;
                                                                                        handleChangeAssistente(servico.id, assistente.id, profissionalObj);
                                                                                    }}
                                                                                    disabled={!servico.editar || isFieldsDisabled()}
                                                                                >
                                                                                    <option value="" hidden>Selecione...</option>
                                                                                    {assistente.profissional && (
                                                                                        <option value={assistente.profissional.value}>
                                                                                            {assistente.profissional.label}
                                                                                        </option>
                                                                                    )}
                                                                                    {colaboradoresDisponiveis.map((option, index) => (
                                                                                        <option key={index} value={option.id}>
                                                                                            {option.nome}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-outline-danger ms-2"
                                                                                    onClick={() => handleRemoverAssistente(servico.id, assistente.id)}
                                                                                    disabled={!servico.editar || isFieldsDisabled()}
                                                                                >
                                                                                    <FaUserMinus />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}

                                                                {servico.servico && servico.colaboradoresAssociados.length > 1 && getColaboradoresDisponiveis(servico).length > 0 && (
                                                                    <div className="mt-2">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                                                            onClick={() => handleAdicionarAssistente(servico.id)}
                                                                            disabled={!servico.editar || isFieldsDisabled()}
                                                                        >
                                                                            <FaUserPlus className="me-1" />
                                                                            Adicionar Assistente
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <InputMask
                                                                mask="__:__"
                                                                replacement={{ _: /\d/ }}
                                                                name="tempo"
                                                                className="form-control"
                                                                autoComplete="off"
                                                                value={servico.tempo}
                                                                onChange={(e) => handleChangeServico(servico.id, 'tempo', e.target.value)}
                                                                placeholder="00:00"
                                                                style={{ width: '80px' }}
                                                                disabled={true}
                                                            />
                                                        </td>
                                                        <td>
                                                            <InputMask
                                                                mask="__:__"
                                                                replacement={{ _: /\d/ }}
                                                                name="inicio"
                                                                className="form-control"
                                                                autoComplete="off"
                                                                value={servico.inicio}
                                                                onChange={(e) => handleChangeServico(servico.id, 'inicio', e.target.value)}
                                                                placeholder="00:00"
                                                                style={{ width: '80px' }}
                                                                disabled={!servico.editar || isFieldsDisabled()}
                                                            />
                                                        </td>
                                                        <td>
                                                            <InputMask
                                                                mask="__:__"
                                                                replacement={{ _: /\d/ }}
                                                                name="fim"
                                                                className="form-control"
                                                                autoComplete="off"
                                                                value={servico.fim}
                                                                onChange={(e) => handleChangeServico(servico.id, 'fim', e.target.value)}
                                                                placeholder="00:00"
                                                                style={{ width: '80px' }}
                                                                disabled={!servico.editar || isFieldsDisabled()}
                                                            />
                                                        </td>
                                                        <td>
                                                            <CurrencyInput
                                                                name="valor"
                                                                placeholder="R$ 0,00"
                                                                value={servico.valor}
                                                                decimalsLimit={2}
                                                                decimalSeparator=","
                                                                groupSeparator="."
                                                                prefix="R$ "
                                                                className="form-control"
                                                                disabled={true}
                                                                autoComplete="off"
                                                                style={{ width: '150px' }}
                                                                onValueChange={(value) =>
                                                                    handleChangeServico(servico.id, 'valor', value)
                                                                }
                                                            />
                                                        </td>
                                                        <td className="text-center text-nowrap">
                                                            <button
                                                                className="btn btn-custom-secondary btn-sm"
                                                                type="button"
                                                                onClick={() => handleEditarServico(servico)}
                                                                disabled={isFieldsDisabled()}
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                className="btn btn-custom-secondary btn-sm ms-3"
                                                                type="button"
                                                                onClick={() => handleRemoverServico(servico)}
                                                                disabled={isFieldsDisabled()}
                                                            >
                                                                <FaTrashAlt />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-12 text-end">
                                    <button
                                        className="btn btn-outline-primary"
                                        type="button"
                                        onClick={handleAdicionarServico}
                                        disabled={isFieldsDisabled()}
                                    >
                                        <FaPlus /> Adicionar Serviço
                                    </button>
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Status</label>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }} className="status-grid-mobile">
                                        {statusOptions.map((status) => {
                                            const permittedStatus = getPermittedStatus(agendamento?.status || '');
                                            const isStatusPermitted = agendamento === null
                                                ? status.value === 'agendado'
                                                : permittedStatus.includes(status.value);

                                            return (
                                                <div
                                                    key={status.value}
                                                    className="form-check"
                                                >
                                                    <label
                                                        className={`status-button ${status.className} ${formData.status === status.value ? "selected" : ""} ${!isStatusPermitted ? "disabled" : ""}`}
                                                        style={{ opacity: isStatusPermitted ? 1 : 0.5, cursor: isStatusPermitted ? 'pointer' : 'not-allowed' }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="status"
                                                            id={status.value}
                                                            value={status.value}
                                                            checked={formData.status === status.value}
                                                            onChange={isStatusPermitted ? handleChange : () => { }}
                                                            disabled={!isStatusPermitted}
                                                            className="hidden me-1 form-check-input"
                                                        />
                                                        {status.label}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Observações</label>
                                    <textarea
                                        name="observacao"
                                        rows="4"
                                        className="form-control"
                                        autoComplete="off"
                                        value={formData.observacao}
                                        onChange={handleChange}
                                    ></textarea>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-lg-6 col-12">
                                    {agendamento?.criado_por != null && (
                                        <>
                                            <label className="form-label fw-bold">Criado por: </label>
                                            <span className="form-label"> {agendamento.criado_por}</span>
                                            <br />
                                            <label className="form-label fw-bold">Criado em: </label>
                                            <span className="form-label"> {moment(agendamento.criado_em).format('DD/MM/YYYY HH:mm:ss')}</span>
                                        </>
                                    )}
                                </div>
                                <div className="col-lg-6 col-12">
                                    {agendamento?.alterado_por != null && (
                                        <>
                                            <label className="form-label fw-bold">Atualizado por: </label>
                                            <span className="form-label"> {agendamento.alterado_por}</span>
                                            <br />
                                            <label className="form-label fw-bold">Atualizado em: </label>
                                            <span className="form-label"> {moment(agendamento.alterado_em).format('DD/MM/YYYY HH:mm:ss')}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer mt-4">
                        <div className="row">
                            <div className="col-12 text-end">
                                <button className="btn btn-outline-danger" type="button" onClick={onClose}>
                                    <FaTimes /> Fechar
                                </button>
                                <button className="btn btn-custom-primary ms-3" type="submit" onClick={handleSubmit}>
                                    <FaRegSave /> Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default AgendamentoModal;
